import React, { useState } from "react";

import { Pressable } from "../../primitive";
import { DropdownSelectOption, DropDownSize } from "../DropdownSelect";
import { TriggerDisplayValue } from "./TriggerDisplayValue/TriggerDisplayValue";

type ColorToken =
  | "transparent"
  | "surface-secondary"
  | "surface-dividers"
  | "grayscale-700"
  | "grayscale-500"
  | "status-error-500"
  | "grayscale-1000"
  | "grayscale-0";

function getBaseStyles(isDisabled?: boolean): {
  bg: ColorToken;
  borderColor: ColorToken;
} {
  return isDisabled
    ? {
        bg: "grayscale-500",
        borderColor: "grayscale-500",
      }
    : {
        bg: "grayscale-0",
        borderColor: "grayscale-0",
      };
}

function getHoverStyles({
  variant,
  isViewOnly,
  isDisabled,
}: {
  variant?: "compact" | string;
  isViewOnly?: boolean;
  isDisabled?: boolean;
}): {
  bg: ColorToken;
  borderColor: ColorToken;
} {
  if (isDisabled) return getBaseStyles(true);

  if (variant === "compact") {
    return {
      bg: "surface-secondary",
      borderColor: "transparent",
    };
  }

  return {
    bg: "grayscale-0",
    borderColor: isViewOnly ? "surface-dividers" : "grayscale-700",
  };
}

function getBorderColor({
  variant,
  isDisabled,
  hasError,
  isOpened,
}: {
  variant?: "compact" | string;
  isDisabled?: boolean;
  hasError?: boolean;
  isOpened?: boolean;
}): ColorToken {
  if (variant === "compact") return "transparent";
  if (isDisabled) return "grayscale-500";
  if (hasError) return "status-error-500";
  if (isOpened) return "grayscale-1000";
  return "surface-dividers";
}

function getBorderWidth(
  variant?: "compact" | "default",
  isViewOnly?: boolean,
  isDisabled?: boolean
) {
  if (isDisabled || variant === "compact") return 0;
  return isViewOnly ? 1.5 : 2;
}

type TriggerProps = {
  size?: DropDownSize;
  onPress: () => void;
  testID?: string;
  ariaLabel?: string;
  isDisabled?: boolean;
  isViewOnly?: boolean;
  hasError?: boolean;
  isOpened?: boolean;
  placeholder: string;
  selectedItem: DropdownSelectOption<any> | null;
  includeLeftElementInSelectedValue: boolean;
  hideChevron: boolean;
  hideLabelInDisplay: boolean;
  hideSubLabelInDisplay: boolean;
  variant?: "default" | "compact";
};

export const Trigger = ({
  size,
  onPress,
  testID,
  ariaLabel,
  isDisabled,
  isViewOnly,
  hasError,
  isOpened,
  selectedItem,
  placeholder,
  includeLeftElementInSelectedValue,
  hideChevron,
  hideLabelInDisplay,
  hideSubLabelInDisplay,
  variant,
}: TriggerProps) => {
  const backgroundColor = isDisabled
    ? "grayscale-500"
    : isViewOnly
    ? "surface-secondary"
    : "surface-primary";

  const normalBorderColor = getBorderColor({
    variant,
    isDisabled,
    hasError,
    isOpened,
  });
  const borderWidth = getBorderWidth(variant, isViewOnly, isDisabled);

  const hoverStyles = getHoverStyles({ variant, isDisabled, isViewOnly });

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      aria-label={ariaLabel}
      onPress={onPress}
      disabled={!!isDisabled}
      borderRadius={"2"}
      borderWidth={borderWidth}
      borderColor={normalBorderColor}
      _hover={hoverStyles}
      backgroundColor={backgroundColor}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      testID={testID}
    >
      <TriggerDisplayValue
        selectedItem={selectedItem || null}
        includeLeftElementInSelectedValue={includeLeftElementInSelectedValue}
        isDisabled={!!isDisabled}
        placeholder={placeholder}
        hideLabelInDisplay={hideLabelInDisplay}
        hideSubLabelInDisplay={hideSubLabelInDisplay}
        hideChevron={hideChevron}
        size={size}
        backgroundColor={isHovered ? hoverStyles.bg : backgroundColor}
        testID={testID}
        ariaLabel={ariaLabel}
        variant={variant}
      />
    </Pressable>
  );
};
